import { ArticleNumberLink } from "../DrofusArticleDetails/ArticleNumberLink";
import { ArticleDetailsModal } from "../DrofusArticleDetails/ArticleDetailsModal";
import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Group,
  Modal,
  SegmentedControl,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { AgGridReact } from "ag-grid-react";
import type { ColDef, GridApi, GridOptions } from "ag-grid-enterprise";
import { skipToken } from "@reduxjs/toolkit/query";
import { FaExternalLinkAlt, FaSyncAlt } from "react-icons/fa";

import { openapi } from "../../openapi";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { ValidationDeltaStatusIcon } from "../components/ValidationDeltaStatusIcon";
import { formatShortDate } from "../globalUtils";
import {
  IDrofusLiveOccurrenceRow,
  selectDrofusRoomOccurrencesLiveApiArg,
  selectDrofusRoomOccurrencesLiveListTypeItems,
  selectDrofusRoomOccurrencesLiveResolvedListType,
  selectDrofusRoomOccurrencesLiveSummary,
  selectDrofusRoomOccurrencesLiveVisibleRows,
} from "./drofusRoomOccurrencesLiveSelectors";
import { selectHttpSingleRoomFull } from "../Room/httpRoomSelectors";
import { setDrofusOccurrenceValidationLiveOccurrencesListTypeInRedux } from "../DrofusOccurrenceValidation/drofusOccurrenceValidationSlice";

function liveErrorMessage(error: unknown): string {
  const err = error as {
    data?: {
      message?: unknown;
      error?: { message?: unknown };
    };
    error?: unknown;
  };

  const message =
    typeof err?.data?.error?.message === "string"
      ? err.data.error.message
      : typeof err?.data?.message === "string"
        ? err.data.message
        : typeof err?.error === "string"
          ? err.error
          : null;

  if (!message) return "Ukjent feil ved henting fra dRofus.";

  const decryptFailure =
    message.includes("drofus_api_key_decrypt_failed") ||
    message.includes("Unsupported state or unable to authenticate data");

  if (decryptFailure) {
    return "Kunne ikke dekryptere lagret dRofus API-nøkkel. Kontroller at Functions-miljøet bruker samme SECRETS_KEK som miljøet der nøkkelen ble lagret, eller lagre dRofus API-nøkkelen på nytt.";
  }

  return message;
}

export const DrofusRoomOccurrencesLiveTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const [opened, { open, close }] = useDisclosure(false);
  const gridRef = useRef<AgGridReact<IDrofusLiveOccurrenceRow>>(null);
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [query, setQuery] = useState("");

  const room = useAppSelector(selectHttpSingleRoomFull);
  const liveArg = useAppSelector(selectDrofusRoomOccurrencesLiveApiArg);
  const visibleRows = useAppSelector(selectDrofusRoomOccurrencesLiveVisibleRows);
  const summary = useAppSelector(selectDrofusRoomOccurrencesLiveSummary);
  const activeListType = useAppSelector(selectDrofusRoomOccurrencesLiveResolvedListType);
  const listTypeItems = useAppSelector(selectDrofusRoomOccurrencesLiveListTypeItems);
  const roomTitle = `${room?.roomNumber ?? liveArg?.body.roomNumber ?? "Rom"} ${
    room?.roomName ?? ""
  }`.trim();

  const { isLoading, isFetching, isError, error, refetch } =
    openapi.usePostFunctionsDrofusRoomsOccurrencesLiveQuery(opened && liveArg ? liveArg : skipToken);

  const defaultColDef = useMemo<ColDef<IDrofusLiveOccurrenceRow>>(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      suppressMovable: true,
      suppressHeaderMenuButton: false,
    }),
    []
  );

  const columnDefs = useMemo<ColDef<IDrofusLiveOccurrenceRow>[]>(
    () => [
      {
        headerName: "Artikkelnr",
        field: "compCode",
        width: 200,
        minWidth: 160,
        cellRenderer: ArticleNumberLink,
      },
      {
        headerName: "Artikkelnavn",
        field: "displayName",
        flex: 1,
        minWidth: 280,
        tooltipField: "displayName",

      },
      {
        headerName: "Programmert antall i dRofus",
        field: "quantity",
        width: 250,
        type: "rightAligned",
        valueFormatter: (params) => String(params.value ?? "–"),
      },
      {
        headerName: "Antall i modellen",
        field: "modelQuantityForCompCode",
        width: 210,
        type: "rightAligned",
        cellRenderer: (params: any) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 8,
              height: "100%",
            }}
          >
            {params.data?.modelQuantityDelta != null && <ValidationDeltaStatusIcon
              delta={params.data?.modelQuantityDelta}
              matchTitle="Antall i modellen matcher dRofus"
              missingTitle="Færre i modellen enn i dRofus"
              extraTitle="Flere i modellen enn i dRofus"
            />}
            <span>{params.value ?? "–"}</span>
          </div>
        ),
      },
      {
        headerName: "Δ modell - dRofus",
        field: "modelQuantityDelta",
        width: 150,
        type: "rightAligned",
        cellStyle: (params) => ({
          color: params.value == null ? "#868e96" : params.value > 0 ? "#1971c2" : params.value < 0 ? "#e03131" : "#2f9e44",
          fontWeight: 600,
        }),
        valueFormatter: (params) => String(params.value ?? "–"),
      },
      {
        headerName: "Skal modelleres i dRofus",
        field: "articleToBeDrawn",
        width: 190,
        valueFormatter: (params) => (params.value === true ? "Ja" : params.value === false ? "Nei" : "–"),
      },
      {
        headerName: "Liste",
        field: "equipmentListTypeName",
        width: 190,
        valueFormatter: (params) => String(params.value ?? "–"),
      },
      {
        headerName: "Ansvar",
        field: "articleResponsibility",
        width: 110,
        valueFormatter: (params) => String(params.value ?? "–"),
      },
      {
        headerName: "dRofus ID",
        field: "occurrenceId",
        width: 120,
        type: "rightAligned",
        valueFormatter: (params) => String(params.value ?? "–"),
      },
    ],
    []
  );

  const gridOptions = useMemo<GridOptions>(
    () => ({
      suppressContextMenu: true,
      allowContextMenuWithControlKey: false,
      suppressMovableColumns: true,
      headerHeight: 32,
      rowHeight: 38,
      suppressCellFocus: true,
      suppressRowClickSelection: true,
      getRowId: (params) => params.data.id,
    }),
    []
  );

  const handleSearchChange = (value: string) => {
    setQuery(value);
    gridApi?.setGridOption("quickFilterText", value);
  };

  if (!liveArg) {
    return (
      <Button leftSection={<FaExternalLinkAlt size={13} />} variant="light" size="xs" disabled>
        Hent fra dRofus
      </Button>
    );
  }

  return (
    <>
      <ArticleDetailsModal />
      <Button leftSection={<FaExternalLinkAlt size={13} />} onClick={open} variant="light" size="xs">
        Hent fra dRofus
      </Button>

      <Modal
        opened={opened}
        onClose={close}
        title={`Live forekomster i dRofus, ${roomTitle}`}
        size="90vw"
        centered
        styles={{
          content: { height: "86vh", display: "flex", flexDirection: "column" },
          body: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column" },
          title: { fontWeight: 700, fontSize: 22 },
          header: { background: "#e7f1ff", minHeight: 64 },
        }}
      >
        <Stack gap="md" h="100%" style={{ minHeight: 0 }}>
          <Group justify="space-between" gap="md" wrap="nowrap">
            <SegmentedControl
              value={activeListType ?? ""}
              onChange={(value) =>
                dispatch(setDrofusOccurrenceValidationLiveOccurrencesListTypeInRedux(value || null))
              }
              data={listTypeItems}
              radius="xl"
              size="sm"
              color="blue"
              transitionDuration={120}
              styles={{
                root: { maxWidth: "100%", overflowX: "auto" },
                label: { whiteSpace: "nowrap" },
              }}
            />

            <Stack gap={2} align="flex-end" style={{ flexShrink: 0 }}>
              <Button
                size="xs"
                variant="subtle"
                leftSection={<FaSyncAlt size={12} />}
                loading={isFetching}
                onClick={() => refetch()}
              >
                Oppdater
              </Button>
              {summary.fetchedAt && (
                <Tooltip label={summary.fetchedAt} position="bottom-end" withArrow>
                  <Text size="xs" c="dimmed">
                    Hentet {formatShortDate(summary.fetchedAt)}
                    {summary.elapsedMs != null ? ` (${summary.elapsedMs} ms)` : ""}
                  </Text>
                </Tooltip>
              )}
            </Stack>
          </Group>

          <Group gap="xs" wrap="wrap">
            <Badge variant="outline" color="blue">
              {visibleRows.length} av {summary.rowCount} hovedartikler
            </Badge>
            <Badge variant="light" color="green">
              Kun hovedartikler som skal modelleres
            </Badge>
          </Group>

          <Text size="xs" c="dimmed">Viser hovedartikler med «Skal modelleres = Ja».</Text>

          {isError && (
            <Alert color="red" title="Kunne ikke hente live data">
              {liveErrorMessage(error)}
            </Alert>
          )}

          <TextInput
            value={query}
            onChange={(e) => handleSearchChange(e.currentTarget.value)}
            placeholder="Søk i dRofus-forekomster..."
            size="sm"
          />

          <div className="ag-theme-quartz" style={{ flex: 1, minHeight: 0, width: "100%" }}>
            <AgGridReact<IDrofusLiveOccurrenceRow>
              ref={gridRef}
              rowData={visibleRows}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              gridOptions={gridOptions}
              sideBar={false}
              animateRows={false}
              loading={isLoading}
              overlayNoRowsTemplate="Ingen dRofus-forekomster funnet"
              onGridReady={(params) => {
                setGridApi(params.api);
                if (query) params.api.setGridOption("quickFilterText", query);
              }}
            />
          </div>
        </Stack>
      </Modal>
    </>
  );
};
